/* eslint-disable @typescript-eslint/no-unused-vars */
import { AxiosResponse } from "axios";
import {
  BackOrderReportParams,
  BackOrderReportResponse,
  CreatedProductReportParams,
  CreatedProductReportResponse,
  DOReportParams,
  DOReportResponse,
  POReportParams,
  POReportResponse,
  SalesOrderReportParams,
  SalesOrderReportResponse,
  TransferOrderReportParams,
  TransferOrderReportResponse,
} from "../../@types/DRO/AllReportsResponse";
import { getAuthToken, reportsAxios } from "../axiosInstances";

const getHeaderValue = (headerValue: unknown, fallback = "") => {
  if (typeof headerValue === "string") {
    return headerValue;
  }

  if (Array.isArray(headerValue)) {
    return headerValue.join(", ");
  }

  return fallback;
};

export class AllReportsApi {
  public static api = new AllReportsApi();
  private instance = reportsAxios;

  getToken() {
    return getAuthToken();
  }

  // Get Created Product Report with pagination and filters
  async getCreatedProductReport(
    params: CreatedProductReportParams
  ): Promise<CreatedProductReportResponse> {
    try {
      const response: AxiosResponse<CreatedProductReportResponse> =
        await this.instance.get("erp/created-product-report", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            product_id: params.product_id,
            description: params.description,
            oem_type: params.oem_type,
            status: params.status,
            created_by: params.created_by,
            start_date: params.start_date,
            end_date: params.end_date,
            page: params.page || 1,
            page_size: params.page_size || 50,
            product_type: params.product_type,
            business_type: params.business_type,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching created product report", error);
      throw error;
    }
  }

  // Download Created Product Report as Excel/CSV
  async downloadCreatedProductReport(
    params: Omit<CreatedProductReportParams, "page" | "page_size">
  ): Promise<void> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "erp/created-product-report",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            Accept:
              "text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream",
          },
          params: {
            product_id: params.product_id,
            description: params.description,
            oem_type: params.oem_type,
            status: params.status,
            created_by: params.created_by,
            start_date: params.start_date,
            end_date: params.end_date,
            product_type: params.product_type,
            business_type: params.business_type,
            download: true,
          },
          responseType: "blob",
        }
      );

      this.downloadFile(response, "created-products-report");
    } catch (error) {
      console.error("Error downloading created product report", error);
      throw error;
    }
  }

  // Get PO Report with pagination and filters
  async getPOReport(params: POReportParams): Promise<POReportResponse> {
    try {
      const response: AxiosResponse<POReportResponse> = await this.instance.get(
        "erp/po-report",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            vendor_id: params.vendor_id,
            vendor_name: params.vendor_name,
            product_name: params.product_name,
            status: params.status,
            product_id: params.product_id,
            po_no: params.po_no,
            location: params.location,
            start_date: params.start_date,
            end_date: params.end_date,
            page: params.page || 1,
            page_size: params.page_size || 50,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching PO report", error);
      throw error;
    }
  }

  // Download PO Report as Excel/CSV
  async downloadPOReport(
    params: Omit<POReportParams, "page" | "page_size">
  ): Promise<void> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "erp/po-report",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            Accept:
              "text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream",
          },
          params: {
            vendor_id: params.vendor_id,
            vendor_name: params.vendor_name,
            product_name: params.product_name,
            status: params.status,
            product_id: params.product_id,
            po_no: params.po_no,
            location: params.location,
            start_date: params.start_date,
            end_date: params.end_date,
            download: true,
          },
          responseType: "blob",
        }
      );

      this.downloadFile(response, "po-report");
    } catch (error) {
      console.error("Error downloading PO report", error);
      throw error;
    }
  }

  // Get DO Report with pagination and filters
  async getDOReport(params: DOReportParams): Promise<DOReportResponse> {
    try {
      const response: AxiosResponse<DOReportResponse> = await this.instance.get(
        "erp/do-report",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            vendor_id: params.vendor_id,
            part_code: params.part_code,
            part_name: params.part_name,
            po_no: params.po_no,
            do_no: params.do_no,
            location: params.location,
            start_date: params.start_date,
            end_date: params.end_date,
            page: params.page || 1,
            page_size: params.page_size || 50,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching DO report", error);
      throw error;
    }
  }

  // Download DO Report as Excel/CSV
  async downloadDOReport(
    params: Omit<DOReportParams, "page" | "page_size">
  ): Promise<void> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "erp/do-report",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            Accept:
              "text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream",
          },
          params: {
            vendor_id: params.vendor_id,
            part_code: params.part_code,
            part_name: params.part_name,
            po_no: params.po_no,
            do_no: params.do_no,
            location: params.location,
            start_date: params.start_date,
            end_date: params.end_date,
            download: true,
          },
          responseType: "blob",
        }
      );

      this.downloadFile(response, "do-report");
    } catch (error) {
      console.error("Error downloading DO report", error);
      throw error;
    }
  }

  // Get Back Order Report with pagination and filters
  async getBackOrderReport(
    params: BackOrderReportParams
  ): Promise<BackOrderReportResponse> {
    try {
      const response: AxiosResponse<BackOrderReportResponse> =
        await this.instance.get("erp/back-order-report", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            source_location: params.source_location,
            destination_location: params.destination_location,
            part_code: params.part_code,
            part_name: params.part_name,
            to_no: params.to_no,
            start_date: params.start_date,
            end_date: params.end_date,
            page: params.page || 1,
            page_size: params.page_size || 50,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching back order report", error);
      throw error;
    }
  }

  // Download Back Order Report as Excel/CSV
  async downloadBackOrderReport(
    params: Omit<BackOrderReportParams, "page" | "page_size">
  ): Promise<void> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "erp/back-order-report",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            Accept:
              "text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream",
          },
          params: {
            source_location: params.source_location,
            destination_location: params.destination_location,
            part_code: params.part_code,
            part_name: params.part_name,
            to_no: params.to_no,
            start_date: params.start_date,
            end_date: params.end_date,
            download: true,
          },
          responseType: "blob",
        }
      );

      this.downloadFile(response, "back-order-report");
    } catch (error) {
      console.error("Error downloading back order report", error);
      throw error;
    }
  }

  // Get Transfer Order Report with pagination and filters
  async getTransferOrderReport(
    params: TransferOrderReportParams
  ): Promise<TransferOrderReportResponse> {
    try {
      const response: AxiosResponse<TransferOrderReportResponse> =
        await this.instance.get("erp/transfer-order-report", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            source_location: params.source_location,
            destination_location: params.destination_location,
            part_code: params.part_code,
            part_name: params.part_name,
            to_no: params.to_no,
            start_date: params.start_date,
            end_date: params.end_date,
            page: params.page || 1,
            page_size: params.page_size || 50,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching transfer order report", error);
      throw error;
    }
  }

  // Download Transfer Order Report as Excel/CSV
  async downloadTransferOrderReport(
    params: Omit<TransferOrderReportParams, "page" | "page_size">
  ): Promise<void> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "erp/transfer-order-report",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            Accept:
              "text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream",
          },
          params: {
            source_location: params.source_location,
            destination_location: params.destination_location,
            part_code: params.part_code,
            part_name: params.part_name,
            to_no: params.to_no,
            start_date: params.start_date,
            end_date: params.end_date,
            download: true,
          },
          responseType: "blob",
        }
      );

      this.downloadFile(response, "transfer-order-report");
    } catch (error) {
      console.error("Error downloading transfer order report", error);
      throw error;
    }
  }

  // Get Sales Order Report with pagination and filters
  async getSalesOrderReport(
    params: SalesOrderReportParams
  ): Promise<SalesOrderReportResponse> {
    try {
      const response: AxiosResponse<SalesOrderReportResponse> =
        await this.instance.get("erp/sales-order-report", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            product_id: params.product_id,
            product_name: params.product_name,
            customer_name: params.customer_name,
            location_id: params.location_id,
            sales_order_id: params.sales_order_id,
            status: params.status,
            job_id: params.job_id,
            start_date: params.start_date,
            end_date: params.end_date,
            page: params.page || 1,
            page_size: params.page_size || 50,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching sales order report", error);
      throw error;
    }
  }

  // Download Sales Order Report as Excel/CSV
  async downloadSalesOrderReport(
    params: Omit<SalesOrderReportParams, "page" | "page_size">
  ): Promise<void> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "erp/sales-order-report",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            Accept:
              "text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream",
          },
          params: {
            product_id: params.product_id,
            product_name: params.product_name,
            customer_name: params.customer_name,
            location_id: params.location_id,
            sales_order_id: params.sales_order_id,
            status: params.status,
            job_id: params.job_id,
            start_date: params.start_date,
            end_date: params.end_date,
            download: true,
          },
          responseType: "blob",
        }
      );

      this.downloadFile(response, "sales-order-report");
    } catch (error) {
      console.error("Error downloading sales order report", error);
      throw error;
    }
  }

  // Helper method to handle file downloads with better error handling and file type detection
  private downloadFile(response: AxiosResponse<Blob>, defaultFileName: string) {
    try {
      // Validate response
      if (!response.data || response.data.size === 0) {
        throw new Error("Empty file received");
      }

      // Determine content type and file extension
      const contentType = getHeaderValue(
        response.headers["content-type"],
        "application/octet-stream"
      );
      let fileExtension = ".xlsx"; // Default to Excel

      if (contentType.includes("text/csv")) {
        fileExtension = ".csv";
      } else if (contentType.includes("application/pdf")) {
        fileExtension = ".pdf";
      } else if (contentType.includes("spreadsheetml")) {
        fileExtension = ".xlsx";
      }

      const blob = new Blob([response.data], { type: contentType });

      // Get filename from Content-Disposition header if available
      const contentDisposition = getHeaderValue(
        response.headers["content-disposition"]
      );
      let filename = `${defaultFileName}-${
        new Date().toISOString().split("T")[0]
      }${fileExtension}`;

      if (contentDisposition) {
        // Try different patterns for filename extraction
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        } else {
          // Try UTF-8 encoded filename
          const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
          if (utf8Match && utf8Match[1]) {
            filename = decodeURIComponent(utf8Match[1]);
          }
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();

      // Cleanup with proper timing
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error in downloadFile:", error);
      throw new Error("Failed to download file. Please try again.");
    }
  }
}
